
import { useQuery } from '@tanstack/react-query';
import { fetchBotSignals, fetchCTraderSignalStatuses } from '@/services/signal-service';
import { Signal, SignalAccountStatus } from '@/types/signal';
import { useState } from 'react';

export const useSignalHistory = (botId: string | undefined) => {
  const [activeTab, setActiveTab] = useState<string>("bot-signals");
  
  // Pagination state
  const [botSignalsPage, setBotSignalsPage] = useState<number>(1);
  const [botSignalsPageSize, setBotSignalsPageSize] = useState<number>(10);
  const [ctraderSignalsPage, setCtraderSignalsPage] = useState<number>(1);
  const [ctraderSignalsPageSize, setCtraderSignalsPageSize] = useState<number>(10);
  
  // Fetch regular bot signals with pagination
  const {
    data: botSignalsResponse,
    isLoading: isBotSignalsLoading,
    error: botSignalsError,
    refetch: refetchBotSignals
  } = useQuery({
    queryKey: ['botSignals', botId, botSignalsPage, botSignalsPageSize],
    queryFn: async () => {
      if (!botId) return { data: [], total: 0, page: 1, pageSize: botSignalsPageSize };
      return fetchBotSignals(botId, botSignalsPage, botSignalsPageSize);
    },
    enabled: !!botId
  });
  
  // Fetch CTrader signal statuses with pagination
  const {
    data: ctraderSignalsResponse,
    isLoading: isCtraderSignalsLoading,
    error: ctraderSignalsError,
    refetch: refetchCtraderSignals
  } = useQuery({
    queryKey: ['ctraderSignals', botId, ctraderSignalsPage, ctraderSignalsPageSize],
    queryFn: async () => {
      if (!botId) return { data: [], total: 0, page: 1, pageSize: ctraderSignalsPageSize };
      return fetchCTraderSignalStatuses(botId, ctraderSignalsPage, ctraderSignalsPageSize);
    },
    enabled: !!botId && activeTab === "ctrader-signals"
  });
  
  // Extract values with defaults
  const botSignals = botSignalsResponse?.data || [];
  const botSignalsTotal = botSignalsResponse?.total || 0;
  const ctraderSignals = ctraderSignalsResponse?.data || [];
  const ctraderSignalsTotal = ctraderSignalsResponse?.total || 0;
  
  // Pagination handlers
  const handleBotSignalsPageChange = (page: number) => {
    setBotSignalsPage(page);
  };
  
  const handleBotSignalsPageSizeChange = (pageSize: number) => {
    setBotSignalsPageSize(pageSize);
    setBotSignalsPage(1); // Reset to first page when changing page size
  };
  
  const handleCtraderSignalsPageChange = (page: number) => {
    setCtraderSignalsPage(page);
  };
  
  const handleCtraderSignalsPageSizeChange = (pageSize: number) => {
    setCtraderSignalsPageSize(pageSize);
    setCtraderSignalsPage(1); // Reset to first page when changing page size
  };
  
  // Refetch both types of signals
  const refreshSignals = () => {
    refetchBotSignals();
    if (activeTab === "ctrader-signals") {
      refetchCtraderSignals();
    }
  };
  
  return {
    botSignals,
    ctraderSignals,
    botSignalsTotal,
    ctraderSignalsTotal,
    botSignalsPage,
    botSignalsPageSize,
    ctraderSignalsPage,
    ctraderSignalsPageSize,
    isBotSignalsLoading,
    isCtraderSignalsLoading,
    botSignalsError,
    ctraderSignalsError,
    activeTab,
    setActiveTab,
    handleBotSignalsPageChange,
    handleBotSignalsPageSizeChange,
    handleCtraderSignalsPageChange,
    handleCtraderSignalsPageSizeChange,
    refreshSignals
  };
};
